import { useState } from 'react'
import { InlineEditField } from './InlineEditField'

export interface MultiContactFieldProps {
  /** "Mobile" or "Email" — the extra rows are labelled Mobile1, Mobile2, … */
  label: string
  fieldNames: [string, string, string]
  tableName: string
  entityId: string
  values: [string, string, string]
  onChange: (index: 0 | 1 | 2, value: string) => void
  placeholder?: string
  errors?: string[]
}

/**
 * The +/- rows from EntityEditDetails.js (`#addMobile`, `#minusMobile`, `#addEmail`…).
 *
 * Three slots exist at all times; the extra two start hidden and are revealed on
 * demand. A row that already holds a value is shown from the start. Removing a
 * row clears its value, matching the legacy behaviour.
 */
export function MultiContactField({
  label,
  fieldNames,
  tableName,
  entityId,
  values,
  onChange,
  placeholder,
  errors = [],
}: MultiContactFieldProps) {
  const [visibleCount, setVisibleCount] = useState(() => {
    if (values[2]) return 3
    if (values[1]) return 2
    return 1
  })

  const addRow = () => setVisibleCount((c) => Math.min(3, c + 1))

  const removeRow = (index: 1 | 2) => {
    // Removing the second row also clears the third, as the legacy #minusMobile did.
    if (index === 1) {
      onChange(1, '')
      onChange(2, '')
      setVisibleCount(1)
    } else {
      onChange(2, '')
      setVisibleCount(2)
    }
  }

  return (
    <div>
      {Array.from({ length: visibleCount }, (_, i) => i as 0 | 1 | 2).map((index) => (
        <div className="multi-field-row" key={index}>
          <div className="field-body">
            <InlineEditField
              label={index === 0 ? label : `${label}${index}`}
              fieldName={fieldNames[index]}
              tableName={tableName}
              entityId={entityId}
              value={values[index]}
              onChange={(v) => onChange(index, v)}
              error={errors[index]}
            >
              {({ disabled }) => (
                <input
                  type="text"
                  className="form-control"
                  autoComplete="off"
                  placeholder={placeholder}
                  disabled={disabled}
                  value={values[index]}
                  onChange={(e) => onChange(index, e.target.value)}
                />
              )}
            </InlineEditField>
          </div>

          <div style={{ display: 'flex', gap: 4, paddingBottom: 12 }}>
            {index > 0 && (
              <button
                type="button"
                className="btn btn-icon"
                title={`Remove ${label}${index}`}
                onClick={() => removeRow(index as 1 | 2)}
              >
                −
              </button>
            )}
            {index === visibleCount - 1 && visibleCount < 3 && (
              <button type="button" className="btn btn-icon" title={`Add another ${label}`} onClick={addRow}>
                +
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
